/*
 * The details column, as a request rather than as a place (UX §2). Nothing is shown there until
 * something asks for it: a thread, a profile, a conversation's details. That is the whole
 * difference to what stood here before — a column that was always present and always said "This
 * view has no details."
 *
 * One request at a time, because the column is one column: opening a profile over a thread replaces
 * it, and `toggle` with the request that is already open closes it (the way clicking a person's
 * avatar twice does in every chat application).
 *
 * Focus is the part worth reading. `open()` remembers what had focus, the shell moves focus into
 * the pane once it is there, and `close()` gives it back — but only if the pane still has it.
 * A close that comes from somewhere else (a navigation) must not pull the reader's focus out of
 * whatever they moved on to.
 */

import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import { MAIN_ID } from "./layout.ts"

export interface DetailsRequest {
	/** The id of a registered view: "chat.thread", "chat.conversation", "core.profile". */
	view: string
	/** What that view needs to render: { conv, rootId } · { conv } · { userId }. */
	params: Record<string, string | number>
}

export interface DetailsApi {
	request: DetailsRequest | null
	/**
	 * Shows a view, replacing whatever was open, and remembers the opener for the way back.
	 *
	 * `focus: false` is for a column the reader did not ask for — one a screen opens *with* the page,
	 * because it belongs to what they came to read (S-01-D6). Such a column opens where they are
	 * looking: the focus stays in the main region, so a screen reader goes on reading the page
	 * instead of announcing a panel nobody pressed a button for. Every column a control opens takes
	 * the focus, which is the default and what a press has to do.
	 */
	open(request: DetailsRequest, options?: { focus?: boolean }): void
	/** The same view with the same parameters closes; anything else opens. */
	toggle(request: DetailsRequest): void
	close(): void
	/** True while what is open was opened by the page itself and must not be focused. */
	quiet: boolean
}

const CLOSED: DetailsApi = {
	request: null,
	open: () => undefined,
	toggle: () => undefined,
	close: () => undefined,
	quiet: false,
}

const DetailsContext = createContext<DetailsApi>(CLOSED)

/** True when two requests name the same view of the same thing. */
function sameRequest(a: DetailsRequest, b: DetailsRequest): boolean {
	if (a.view !== b.view) return false
	const keys = Object.keys(a.params)
	if (keys.length !== Object.keys(b.params).length) return false
	return keys.every((key) => a.params[key] === b.params[key])
}

/**
 * Holds the one request the shell renders. It is a provider rather than a module-level store
 * because two shells in one document — which is what a test renders — must not share it.
 */
export function DetailsProvider({ children }: { children: ReactNode }) {
	const [request, setRequest] = useState<DetailsRequest | null>(null)
	// Whether what is open was opened by the page rather than by a press. It is state and not a ref
	// because the shell's focus effect reads it in the same commit as the request it belongs to.
	const [quiet, setQuiet] = useState(false)
	// What had focus when the pane was opened. It is read on close, so it survives every render in
	// between and belongs in a ref rather than in state.
	const opener = useRef<HTMLElement | null>(null)

	const open = useCallback((next: DetailsRequest, options?: { focus?: boolean }) => {
		// Only a control counts as an opener. A pane opened by the address — a permalink, a reload,
		// `?thread=` — is opened while nothing has focus, and `document.body` is an HTMLElement that
		// cannot take it: recording it would rob the close of its #tb-main fallback and leave the
		// reader tabbing from the top of the page.
		const active = document.activeElement
		opener.current = active instanceof HTMLElement && active !== document.body ? active : null
		setQuiet(options?.focus === false)
		setRequest(next)
	}, [])

	const close = useCallback(() => {
		if (request === null) return
		// Only take the focus back if the pane still has it: a close caused by a navigation happens
		// while the reader is already somewhere else, and moving them again is worse than leaving
		// them where they are.
		const active = document.activeElement
		const inPane =
			active === null ||
			active === document.body ||
			(active instanceof HTMLElement && active.closest(".tb-region--details, .tb-details") !== null)
		if (inPane) {
			const back = opener.current
			const target = back !== null && back.isConnected ? back : document.getElementById(MAIN_ID)
			target?.focus()
		}
		opener.current = null
		setQuiet(false)
		setRequest(null)
	}, [request])

	const toggle = useCallback(
		(next: DetailsRequest) => {
			if (request !== null && sameRequest(request, next)) close()
			else open(next)
		},
		[request, open, close],
	)

	const api = useMemo<DetailsApi>(
		() => ({ request, open, toggle, close, quiet }),
		[request, open, toggle, close, quiet],
	)
	return <DetailsContext.Provider value={api}>{children}</DetailsContext.Provider>
}

/**
 * The details column, from anywhere inside the shell. Outside one it answers a closed column whose
 * three methods do nothing, so a surface that can also be rendered without a shell — a message row
 * in a unit test — needs no null check of its own.
 */
export function useDetails(): DetailsApi {
	return useContext(DetailsContext)
}
